<template>
  <div class="navigation" :class="{ 'nav-opened': navOpen }">
    <div class="navigation-shell">
      <div class="navigation-button menu-button" @click="toggleNav()">
        <div class="icon-chip menu-chip">
          <span v-if="navOpen" class="mdi mdi-backburger" aria-hidden="true" />
          <span v-else class="mdi mdi-menu" aria-hidden="true" />
        </div>
        <div class="copy">{{ $t("menu.menuName") }}</div>
      </div>

      <div class="navigation-items">
        <NavigationButton
          v-for="item in visibleNavItems"
          :key="item.routeName"
          :title="navItemTitle(item)"
          :routeName="item.routeName"
          :icon="item.icon"
          @click.native="goToRoute(item.routeName)"
        />
        <NavigationButton
          v-if="hasAnyOperatorAccess"
          :title="operatorTitle"
          :routeName="routeNames.operator"
          icon="mdi-monitor-dashboard"
          @click.native="goToRoute(routeNames.operator)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { routeNames } from '../js/routeNames';
import { NAV_ITEMS } from '../js/navConfig';
import NavigationButton from './NavigationButton.vue';
import { decryptData } from '../js/utils/encrypt';

export default {
  name: 'Navigation',
  components: {
    NavigationButton,
  },
  computed: {
    routeNames: () => routeNames,
    visibleNavItems() {
      return NAV_ITEMS.filter((item) => {
        if (!item.permission) return true;
        return (this.permissions || []).includes(item.permission);
      });
    },
    hasGateOperatorAccess() {
      const perms = this.permissions || [];
      const byPerm = perms.includes('Gate-Operator') || perms.includes('View-Gate');
      const roleName = (this.role || '').toString().toLowerCase();
      const byRole = roleName.includes('gate') && roleName.includes('operator');
      return byPerm || byRole;
    },
    hasClaimOperatorAccess() {
      const perms = this.permissions || [];
      const byPerm = perms.includes('Claim-Operator') || perms.includes('View-Claim');
      const roleName = (this.role || '').toString().toLowerCase();
      const byRole = roleName.includes('claim') && roleName.includes('operator');
      return byPerm || byRole;
    },
    hasAnyOperatorAccess() {
      return this.hasGateOperatorAccess || this.hasClaimOperatorAccess;
    },
    operatorTitle() {
      if (this.hasGateOperatorAccess && this.hasClaimOperatorAccess) return 'Operator';
      if (this.hasGateOperatorAccess) return 'Gate Operator';
      if (this.hasClaimOperatorAccess) return 'Claim Operator';
      return 'Operator';
    },
  },
  data() {
    return {
      navOpen: false,
      permissions: [],
      role: '',
    };
  },
  mounted() {
    let mkLocalData = localStorage.getItem('Permissions');
    if (!mkLocalData) {
      mkLocalData = 'null';
    }
    const secretPermissionKey = process.env.KEY || 'myscecretkey';
    this.permissions = decryptData(mkLocalData, secretPermissionKey) || [];

    let mkRole = localStorage.getItem('Role');
    if (!mkRole) {
      mkRole = 'null';
    }
    this.role = decryptData(mkRole, secretPermissionKey);
  },
  methods: {
    navItemTitle(item) {
      if (item.titleKey) return this.$t(item.titleKey);
      return item.title || '';
    },
    goToRoute(route) {
      if (this.navOpen) this.toggleNav();
      if (route === this.$router.currentRoute.name) return;
      this.$router.push({ name: route });
    },
    toggleNav() {
      this.navOpen = !this.navOpen;
    },
  },
};
</script>

<style scoped lang="scss">
.navigation {
  width: 60px;
  min-width: 60px;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
  z-index: 100;
  background-color: $brand-primary;
  overflow: hidden;
  transition: width 300ms ease-in-out, min-width 300ms ease-in-out;

  &.nav-opened {
    width: 220px;
    min-width: 220px;
    box-shadow: 4px 0 18px rgba(0, 0, 0, 0.18);
  }

  .navigation-shell {
    width: 220px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .navigation-button.menu-button {
    flex-shrink: 0;
    min-height: 48px;
    margin-top: 8px;

    .menu-chip .mdi {
      font-size: 22px;
    }
  }

  .navigation-items {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 6px 0 12px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba($general-white, 0.25);
      border-radius: 4px;
    }
  }

  /* Collapsed: icon-only rail */
  &:not(.nav-opened) {
    .navigation-shell {
      width: 60px;
    }

    .navigation-button.menu-button,
    ::v-deep .navigation-button {
      justify-content: center;
      padding: 4px 0;
      gap: 0;
    }

    .navigation-button.menu-button .copy,
    ::v-deep .navigation-button .copy {
      display: none;
    }

    ::v-deep .navigation-button .icon-chip {
      margin: 0 auto;
    }
  }

  &.nav-opened {
    .navigation-button.menu-button,
    ::v-deep .navigation-button {
      justify-content: flex-start;
    }
  }

  .navigation-button.menu-button {
    width: 100%;
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 10px 4px 12px;
    background: transparent;
    transition: background-color 150ms ease;

    &:hover {
      background-color: rgba($general-white, 0.08);

      .copy {
        opacity: 1;
      }

      .icon-chip {
        background-color: rgba($general-white, 0.18);
      }
    }

    .icon-chip {
      width: 36px;
      height: 36px;
      min-width: 36px;
      border-radius: 10px;
      background-color: rgba($general-white, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      color: $general-white;
    }

    .copy {
      flex: 1;
      text-align: left;
      font-size: 13px;
      font-weight: 600;
      color: $general-white;
      opacity: 0.88;
      white-space: nowrap;
    }
  }
}
</style>
